import { INTERIOR_GRID_H, INTERIOR_GRID_W } from "./constants.js";
import { resolveInteriorTheme } from "./building-interior-theme.js";
function mulberry32(a) {
    return function () {
        let t = (a += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** FNV-1a style — browser-safe (no node:crypto). */
function hashSeed(seedId, buildingId) {
    let h = 2166136261;
    const s = `${seedId}|interior|${buildingId}`;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}
function emptyGrid(w, h) {
    return Array.from({ length: h }, () => Array.from({ length: w }, () => false));
}
function inBounds(tx, ty, w, h) {
    return tx >= 0 && ty >= 0 && tx < w && ty < h;
}
function setWalk(grid, tx, ty) {
    if (inBounds(tx, ty, grid[0].length, grid.length))
        grid[ty][tx] = true;
}
function isWalk(grid, tx, ty) {
    return inBounds(tx, ty, grid[0].length, grid.length) && grid[ty][tx] === true;
}
/** BFS from start; returns reachable walkable tiles. */
/** Manhattan corridor between two tiles (opens doors in walls). */
function carveCorridor(grid, from, to) {
    const carveAt = (cx, cy) => {
        setWalk(grid, cx, cy);
        if (cx + 1 < grid[0].length)
            setWalk(grid, cx + 1, cy);
        if (cy + 1 < grid.length)
            setWalk(grid, cx, cy + 1);
    };
    let x = from.tile_x;
    let y = from.tile_y;
    carveAt(x, y);
    while (x !== to.tile_x) {
        x += x < to.tile_x ? 1 : -1;
        carveAt(x, y);
    }
    while (y !== to.tile_y) {
        y += y < to.tile_y ? 1 : -1;
        carveAt(x, y);
    }
}
function reachableFrom(grid, start) {
    const seen = new Set();
    const out = [];
    const q = [start];
    seen.add(`${start.tile_x},${start.tile_y}`);
    while (q.length) {
        const c = q.shift();
        out.push(c);
        for (const [dx, dy] of [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
        ]) {
            const nx = c.tile_x + dx;
            const ny = c.tile_y + dy;
            const k = `${nx},${ny}`;
            if (seen.has(k) || !isWalk(grid, nx, ny))
                continue;
            seen.add(k);
            q.push({ tile_x: nx, tile_y: ny });
        }
    }
    return out;
}
function tileKey(t) {
    return `${t.tile_x},${t.tile_y}`;
}
function manhattanTiles(a, b) {
    return Math.abs(a.tile_x - b.tile_x) + Math.abs(a.tile_y - b.tile_y);
}
function isExcludedTile(t, exclude) {
    return exclude.some((e) => e.tile_x === t.tile_x && e.tile_y === t.tile_y);
}
/** Multi-source BFS from walls; walkable tiles get distance to nearest non-walkable. */
function distanceToWallField(grid) {
    const h = grid.length;
    const w = grid[0].length;
    const dist = Array.from({ length: h }, () => Array.from({ length: w }, () => -1));
    const q = [];
    for (let ty = 0; ty < h; ty++) {
        for (let tx = 0; tx < w; tx++) {
            if (!isWalk(grid, tx, ty)) {
                dist[ty][tx] = 0;
                q.push({ tile_x: tx, tile_y: ty });
            }
        }
    }
    let qi = 0;
    while (qi < q.length) {
        const { tile_x, tile_y } = q[qi++];
        const nd = dist[tile_y][tile_x] + 1;
        for (const [dx, dy] of [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
        ]) {
            const nx = tile_x + dx;
            const ny = tile_y + dy;
            if (!inBounds(nx, ny, w, h) || dist[ny][nx] >= 0 || !isWalk(grid, nx, ny))
                continue;
            dist[ny][nx] = nd;
            q.push({ tile_x: nx, tile_y: ny });
        }
    }
    return dist;
}
function labelReachableComponents(grid, candidates) {
    const candidateSet = new Set(candidates.map(tileKey));
    const labels = new Map();
    let compId = 0;
    for (const start of candidates) {
        const sk = tileKey(start);
        if (labels.has(sk))
            continue;
        const q = [start];
        labels.set(sk, compId);
        let qi = 0;
        while (qi < q.length) {
            const c = q[qi++];
            for (const [dx, dy] of [
                [1, 0],
                [-1, 0],
                [0, 1],
                [0, -1],
            ]) {
                const nx = c.tile_x + dx;
                const ny = c.tile_y + dy;
                const k = `${nx},${ny}`;
                if (!candidateSet.has(k) || labels.has(k) || !isWalk(grid, nx, ny))
                    continue;
                labels.set(k, compId);
                q.push({ tile_x: nx, tile_y: ny });
            }
        }
        compId++;
    }
    return labels;
}
/** Pick the most interior reachable tile — room center, not edge/corridor wall. */
function pickRoomCenterTile(grid, candidates, exclude, tieBreakFrom) {
    const filtered = candidates.filter((t) => !isExcludedTile(t, exclude));
    if (filtered.length === 0)
        return undefined;
    const wallDist = distanceToWallField(grid);
    const clearance = (t) => wallDist[t.tile_y]?.[t.tile_x] ?? 0;
    const maxClearance = Math.max(...filtered.map(clearance));
    const minClearance = maxClearance >= 2 ? 2 : maxClearance;
    const labels = labelReachableComponents(grid, filtered);
    const componentStats = new Map();
    for (const t of filtered) {
        const id = labels.get(tileKey(t));
        const c = clearance(t);
        const cur = componentStats.get(id) ?? { size: 0, bestClearance: 0 };
        cur.size++;
        cur.bestClearance = Math.max(cur.bestClearance, c);
        componentStats.set(id, cur);
    }
    const scoreTile = (t) => {
        const id = labels.get(tileKey(t));
        const comp = componentStats.get(id);
        return {
            t,
            clearance: clearance(t),
            compSize: comp.size,
            compBest: comp.bestClearance,
            dist: tieBreakFrom ? manhattanTiles(t, tieBreakFrom) : 0,
        };
    };
    const qualified = filtered.filter((t) => clearance(t) >= minClearance).map(scoreTile);
    const pool = qualified.length > 0 ? qualified : filtered.map(scoreTile);
    pool.sort((a, b) => {
        if (b.clearance !== a.clearance)
            return b.clearance - a.clearance;
        if (b.compBest !== a.compBest)
            return b.compBest - a.compBest;
        if (b.compSize !== a.compSize)
            return b.compSize - a.compSize;
        return b.dist - a.dist;
    });
    return pool[0]?.t;
}
/** Wall distance for a walkable tile (exported for layout tests). */
export function wallClearanceAt(grid, tx, ty) {
    return distanceToWallField(grid)[ty]?.[tx] ?? -1;
}
/**
 * Deterministic interior floor plan: outer walls, 1–2 room dividers with door gaps,
 * exit on a perimeter wall, spawn just inside the exit.
 */
export function generateBuildingInterior(opts) {
    const w = INTERIOR_GRID_W;
    const h = INTERIOR_GRID_H;
    const rng = mulberry32(hashSeed(opts.seedId, opts.buildingId));
    const layout = resolveInteriorTheme(opts.buildingId, true).layout;
    const grid = emptyGrid(w, h);
    for (let ty = 1; ty < h - 1; ty++) {
        for (let tx = 1; tx < w - 1; tx++) {
            setWalk(grid, tx, ty);
        }
    }
    const midY = Math.floor(h / 2);
    const midX = Math.floor(w / 2);
    const hDoorX = Math.floor(w / 2 + (rng() - 0.5) * 4);
    const vDoorY = Math.floor(h / 2 + (rng() - 0.5) * 3);
    const hDoorWide = (tx) => tx === hDoorX || tx === hDoorX + 1;
    const vDoorWide = (ty) => ty === vDoorY || ty === vDoorY + 1;
    if (rng() > 1 - layout.horizontalDividerChance) {
        for (let tx = 1; tx < w - 1; tx++) {
            if (hDoorWide(tx))
                continue;
            grid[midY][tx] = false;
        }
    }
    if (rng() > 1 - layout.verticalDividerChance) {
        for (let ty = 1; ty < h - 1; ty++) {
            if (vDoorWide(ty))
                continue;
            grid[ty][midX] = false;
        }
    }
    let exit_wall;
    if (layout.allowNorthSouthExit && rng() < 0.2) {
        exit_wall = rng() < 0.5 ? "north" : "south";
    }
    else {
        exit_wall = rng() < 0.5 ? "west" : "east";
    }
    let exit_tile;
    if (exit_wall === "east") {
        exit_tile = { tile_x: w - 1, tile_y: midY };
        setWalk(grid, w - 1, midY);
    }
    else if (exit_wall === "west") {
        exit_tile = { tile_x: 0, tile_y: midY };
        setWalk(grid, 0, midY);
    }
    else if (exit_wall === "north") {
        exit_tile = { tile_x: midX, tile_y: 0 };
        setWalk(grid, midX, 0);
    }
    else {
        exit_tile = { tile_x: midX, tile_y: h - 1 };
        setWalk(grid, midX, h - 1);
    }
    const spawnOffsets = {
        south: [0, -2],
        north: [0, 2],
        east: [-2, 0],
        west: [2, 0],
    };
    const [sdx, sdy] = spawnOffsets[exit_wall];
    let spawn_tile = {
        tile_x: exit_tile.tile_x + sdx,
        tile_y: exit_tile.tile_y + sdy,
    };
    if (!isWalk(grid, spawn_tile.tile_x, spawn_tile.tile_y)) {
        spawn_tile = { ...exit_tile };
    }
    setWalk(grid, spawn_tile.tile_x, spawn_tile.tile_y);
    const reach = reachableFrom(grid, spawn_tile);
    if (!reach.some((t) => t.tile_x === exit_tile.tile_x && t.tile_y === exit_tile.tile_y)) {
        setWalk(grid, exit_tile.tile_x, exit_tile.tile_y);
    }
    const hub = { tile_x: midX, tile_y: midY };
    let content_tile;
    let npc_tile;
    if (opts.hasContent || opts.hasNpc) {
        carveCorridor(grid, spawn_tile, hub);
        const reachAfterExit = reachableFrom(grid, spawn_tile);
        const fallback = isWalk(grid, hub.tile_x, hub.tile_y) ? hub : spawn_tile;
        if (opts.hasContent) {
            content_tile =
                pickRoomCenterTile(grid, reachAfterExit, [spawn_tile, exit_tile], spawn_tile) ??
                    fallback;
        }
        if (opts.hasNpc) {
            const exclude = [spawn_tile, exit_tile];
            if (content_tile)
                exclude.push(content_tile);
            npc_tile =
                pickRoomCenterTile(grid, reachAfterExit, exclude, spawn_tile) ??
                    (content_tile ? fallback : pickRoomCenterTile(grid, reachAfterExit, [spawn_tile, exit_tile], spawn_tile) ?? fallback);
        }
    }
    return {
        gridW: w,
        gridH: h,
        walkable: grid,
        exit_tile,
        exit_wall,
        spawn_tile,
        content_tile,
        npc_tile,
    };
}
export function interiorHasContentMarker(spec) {
    return spec.content_tile != null;
}
export function interiorNpcTile(spec) {
    return spec.npc_tile ?? spec.content_tile;
}
